import React, { useState, useEffect } from 'react';
import azureDevOpsService from './services/azureDevOpsService';

function lastWeekDate() {
  return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
}

export default function KpiCreatedBugs() {
  const today = new Date().toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(lastWeekDate());
  const [endDate, setEndDate] = useState(today);
  const [created, setCreated] = useState(null);
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleCalculate() {
    setLoading(true);
    setError(null);
    try {
      const [c, r] = await Promise.all([
        azureDevOpsService.getCreatedBugsCount(startDate, endDate),
        azureDevOpsService.getResolvedBugsCount(startDate, endDate),
      ]);
      setCreated(c);
      setResolved(r);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { handleCalculate(); }, []);

  function durationDays(start, end) {
    if (!start || !end) return null;
    return Math.round((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24));
  }

  const duration = durationDays(startDate, endDate);
  const balance = (resolved?.bugCount ?? 0) - (created?.bugCount ?? 0);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Bugs créés / résolus</h2>
        <p className="text-sm text-gray-500 mt-0.5">Tribu Argonos · Bugs créés et résolus (Done) pendant la période</p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Date début
          </label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Date fin
          </label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={handleCalculate}
          disabled={loading}
          className="px-5 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
        >
          {loading ? 'Chargement…' : 'Calculer'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          Erreur : {error}
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center h-36 text-gray-400 gap-3">
          <svg className="animate-spin h-7 w-7 text-blue-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">Chargement…</span>
        </div>
      )}

      {created && resolved && !loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-red-50 to-red-100 p-5 rounded-xl border-2 border-red-200">
            <div className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-1">Bugs créés</div>
            <div className="text-5xl font-bold text-red-700 leading-none">{created.bugCount || 0}</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-xl border-2 border-green-200">
            <div className="text-xs font-semibold text-green-500 uppercase tracking-wide mb-1">Bugs résolus</div>
            <div className="text-5xl font-bold text-green-700 leading-none">{resolved.bugCount || 0}</div>
          </div>
          <div className={`p-5 rounded-xl border-2 ${balance >= 0 ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200' : 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200'}`}>
            <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${balance >= 0 ? 'text-emerald-500' : 'text-amber-500'}`}>
              Balance
            </div>
            <div className={`text-5xl font-bold leading-none ${balance >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
              {balance >= 0 ? '+' : ''}{balance}
            </div>
            <div className={`text-xs mt-1 ${balance >= 0 ? 'text-emerald-500' : 'text-amber-500'}`}>
              {balance >= 0 ? 'résolus > créés ✓' : 'créés > résolus'}
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl border-2 border-blue-200">
            <div className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-1">Durée</div>
            <div className="text-5xl font-bold text-blue-700 leading-none">{duration ?? '–'}</div>
            <div className="text-sm text-blue-500 mt-1">jours</div>
          </div>
        </div>
      )}
    </div>
  );
}
